/**
 * Debug utility functions
 *
 * Provides:
 * - Debug mode toggle (D key or three-finger tap)
 * - Configurable log levels (0-5)
 * - Integration with LogCapture system for in-game console
 * - Robot3 state machine debugging
 * - Debug UI management
 *
 * Log Levels:
 * 0 - No logs
 * 1 - Errors only
 * 2 - Warnings only
 * 3 - Info only
 * 4 - All logs (errors + warnings + info)
 * 5 - Errors and warnings (no info)
 */

import {d} from 'robot3';

// Robot3 state machine debugging hook
d._onEnter = function(machine, to, state, prevState, event) {
  // Robot3 states are symbols or strings, 'to' is the state name we want
  const fromState = machine.current || 'unknown';
  if (!debugMode ) return;
  debugLog( -1,`Entering state: ${to} from ${fromState} with event: ${event}`, machine);
}

let debugMode = false;

// Load log level from localStorage (persistent between sessions)
let logLevel = parseInt(localStorage.getItem('soulforge_logLevel') || '1'); // Default: errors only

/**
 * Set the debug log level (0-5)
 * @param {number} level - Log level (0=none, 1=errors, 2=warnings, 3=info, 4=all, 5=errors+warnings)
 */
export function setLogLevel(level) {
  logLevel = level;
}

/**
 * Check if debug mode is currently enabled
 * @returns {boolean} True if debug mode is active
 */
export function isDebugMode() {
  return debugMode;
}

/**
 * Check if info-level logs should be shown based on current log level
 * @returns {boolean} True if info logs should be displayed
 * @private
 */
function shouldShowInfoLogs() {
  // Show info logs when:
  // logLevel === 3 (Info only)
  // logLevel === 4 (All logs)
  return (logLevel === 3 || logLevel === 4);
}

let debugUIInitialized = false;

/**
 * Enable or disable debug mode
 *
 * When enabled:
 * - Shows debug panel/stats window
 * - Initializes debug visualization UI (only once)
 * - Enables debug rendering (targeting lines, perception ranges, etc.)
 *
 * @param {boolean} enabled - True to enable debug mode, false to disable
 */
export function setDebugMode(enabled) {
  debugMode = enabled;
  
  // Try to use StatsWindow if available
  if (window.statsWindow) {
    window.statsWindow.setDebugPanelVisibility(enabled);
    
    // Initialize debug visualization UI only once
    if (enabled && !debugUIInitialized) {
      debugUIInitialized = true;
      const elements = window.statsWindow.getElements();
      import('../systems/DebugVisualizationUI.js').then(module => {
        module.debugVisualizationUI.init(elements.debugPanel);
      });
    }
  } else {
    // Fallback to old system if StatsWindow not available
    const debugPanel = document.getElementById('debugPanel');
    const debugToggle = document.getElementById('debugModeToggle');
    
    // Update checkbox to match current state
    if (debugToggle) {
      debugToggle.checked = enabled;
    }
    
    if (debugPanel) {
      if (enabled) {
        // Show debug panel
        debugPanel.style.display = 'block';
        
        // Initialize debug visualization UI only once
        if (!debugUIInitialized) {
          debugUIInitialized = true;
          import('../systems/DebugVisualizationUI.js').then(module => {
            module.debugVisualizationUI.init(debugPanel);
          });
        }
      } else {
        // Hide debug panel
        debugPanel.style.display = 'none';
      }
    }
  }
}

/**
 * Toggle debug mode on/off
 * Persists state to localStorage for next session
 */
export function toggleDebugMode() {
  setDebugMode(!debugMode);
  // Save debug visibility state
  localStorage.setItem('soulforge_debugVisible', debugMode ? 'true' : 'false');
}

/**
 * Log debug information (info level)
 *
 * Only logs when:
 * - Debug mode is enabled
 * - Soldier level >= 2 (filters out level 1 spam)
 * - Log level allows info logs (3 or 4)
 *
 * Integrates with LogCapture for in-game console display.
 *
 * @param {number} level - Soldier level (used for filtering)
 * @param {Object} soldierData - Debug data object to log
 */
export function debugLog(level, soldierData) {
  if (!debugMode || level < 2) return;
  if (!shouldShowInfoLogs()) return;
  
  const message = `L${level} soldier debug:`;
  console.log(message, soldierData);
  
  // Add to log capture if available
  if (window.logCapture && window.logCapture.captureEnabled) {
    window.logCapture.addDebugLog('log', message, soldierData);
  }
}

/**
 * Log debug warning (warning level)
 *
 * Only logs when:
 * - Debug mode is enabled
 * - Soldier level >= 2 (filters out level 1 spam)
 * - Log level allows warnings (2, 4, or 5)
 *
 * Use for non-critical issues that should be investigated.
 *
 * @param {number} level - Soldier level (used for filtering)
 * @param {string} message - Warning message
 * @param {Object} data - Additional debug data
 */
export function debugWarn(level, message, data) {
  if (!debugMode || level < 2) return;
  // Check log level settings
  if (logLevel === 0) return; // No logs
  if (logLevel === 1) return; // Errors only
  if (logLevel === 3) return; // Info only
  if (logLevel === 4 || logLevel === 2 || logLevel === 5) {
    const fullMessage = `L${level} ${message}:`;
    console.warn(fullMessage, data);
    
    // Add to log capture if available
    if (window.logCapture && window.logCapture.captureEnabled) {
      window.logCapture.addDebugLog('warn', fullMessage, data);
    }
  }
}

/**
 * Log debug error (error level)
 *
 * Only logs when:
 * - Debug mode is enabled
 * - Log level allows errors (1, 4, or 5)
 *
 * Use for critical issues and bugs that affect game functionality.
 *
 * @param {number} level - Soldier level (for context)
 * @param {string} message - Error message
 * @param {Object} data - Additional error data
 */
export function debugError(level, message, data) {
  if (!debugMode) return;
  // Check log level settings
  if (logLevel === 0) return; // No logs
  if (logLevel === 2) return; // Warnings only
  if (logLevel === 3) return; // Info only
  if (logLevel === 4 || logLevel === 1 || logLevel === 5) {
    const fullMessage = `L${level} ${message}:`;
    console.error(fullMessage, data);
    
    // Add to log capture if available
    if (window.logCapture && window.logCapture.captureEnabled) {
      window.logCapture.addDebugLog('error', fullMessage, data);
    }
  }
}

export function updateDebugStats(stats) {
  if (!debugMode) return;
  const debugStatsEl = document.getElementById('debugStats');
  if (debugStatsEl) {
    let baseInfo = '';
    if (stats.bases) {
      stats.bases.forEach(b => {
        baseInfo += `${b.name}: ${b.health}/${b.maxHealth} HP, ${b.metal} metal<br>`;
      });
    }

    let debugHtml = '<table class="debug-stats-table">';
    debugHtml += '<tr><td>Fighting:</td><td class="debug-value">' + stats.fighting + '</td></tr>';
    debugHtml += '<tr><td>L2+ soldiers:</td><td class="debug-value">' + stats.highLevel + '</td></tr>';
    debugHtml += '<tr><td>L15 (max):</td><td class="debug-value">' + stats.maxLevel + '</td></tr>';
    debugHtml += '<tr><td>Projectiles:</td><td class="debug-value">' + stats.projectiles + '</td></tr>';
    debugHtml += '<tr><td>Broken targeting:</td><td class="debug-value">' + stats.brokenTargeting + '</td></tr>';
    debugHtml += '</table>';
    
    if (baseInfo) {
      debugHtml += '<strong style="display: block; margin-top: 10px;">Base Info:</strong>';
      debugHtml += '<div style="font-size: 10px;">' + baseInfo + '</div>';
    }
    
    debugStatsEl.innerHTML = debugHtml;
  }
}